import { useMemo, useState } from 'react';
import { IconSizes, SearchMdIcon } from '@newcore/icons';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Input } from '@newcore/ui/inputs';
import { SegmentedControl } from '@newcore/ui/segmented-control';
import { Text } from '@newcore/ui/typography';
import { ErrorAlertWithRetry } from '@/components/common/error-alert-with-retry';
import { PageHeader } from '@/components/common/page-header';
import { FilterTabs, type FilterTabOption } from '@/components/ui/filter-tabs';
import { AppViewMode } from '@/enums/app-view-mode.enums';
import { VIEW_MODE_OPTIONS } from '@/features/applications/constants/applications.constants';
import type { ApplicationFilter } from '@/features/applications/types/applications.types';
import { useRecentApps } from '@/hooks/use-recent-apps';
import { useApplicationsQuery } from '@/hooks/use-user-portal.queries';
import { useAppViewModeStore } from '@/stores/app-view-mode.store';
import type { UserApplication } from '@/types/api.types';
import { CatalogBody } from './catalog-body';

const EMPTY_APPLICATIONS: UserApplication[] = [];

interface AllAppsSectionProps {
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

export const AllAppsSection = ({
  onApplicationClick,
  loadingAppId,
}: AllAppsSectionProps) => {
  const {
    data: applications,
    isLoading,
    isError,
    error,
    refetch,
  } = useApplicationsQuery();
  const { recentAppIds } = useRecentApps();
  const viewMode = useAppViewModeStore(state => state.viewMode);
  const setViewMode = useAppViewModeStore(state => state.setViewMode);

  const [filter, setFilter] = useState<ApplicationFilter>('all');
  const [query, setQuery] = useState('');

  const allApps = applications?.items ?? EMPTY_APPLICATIONS;
  const totalCount = applications?.total ?? allApps.length;
  const isTruncated = allApps.length < totalCount;

  const recentApps = useMemo(() => {
    const byId = new Map(allApps.map(app => [app.app_id, app]));

    return recentAppIds
      .map(id => byId.get(id))
      .filter((app): app is UserApplication => Boolean(app));
  }, [allApps, recentAppIds]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();

    return allApps.filter(app => {
      if (filter === 'favorites' && !app.is_favorite) {
        return false;
      }

      if (!term) {
        return true;
      }

      return app.app_name.toLowerCase().includes(term);
    });
  }, [allApps, filter, query]);

  const hasApplicationSearchQuery = query.trim().length > 0;

  const tabs: FilterTabOption<ApplicationFilter>[] = [
    { value: 'all', label: 'All', count: totalCount },
    {
      value: 'favorites',
      label: 'Favorites',
      count: allApps.filter(app => app.is_favorite).length,
    },
  ];

  const showRecent =
    viewMode === AppViewMode.ICONS &&
    filter === 'all' &&
    !hasApplicationSearchQuery &&
    recentApps.length > 0;

  return (
    <div className='flex w-full flex-col gap-12'>
      <PageHeader
        title='Applications'
        description='Everything you have access to. Apps open in a new tab.'
      />

      <div className='flex w-full flex-wrap items-center gap-4 lg:gap-12'>
        <FilterTabs
          className='flex-1'
          options={tabs}
          value={filter}
          onChange={setFilter}
          areCountsLoading={isLoading}
          aria-label='Filter applications'
        />
        <div className='flex w-full items-center gap-2 sm:w-auto'>
          <Input
            type='search'
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder='Search apps'
            aria-label='Search apps'
            className='w-full sm:w-59'
            leading={
              <SearchMdIcon size={IconSizes.SM} className='text-quaternary' />
            }
          />
          <SegmentedControl
            options={VIEW_MODE_OPTIONS}
            value={viewMode}
            onChange={setViewMode}
            size={Sizes.MD}
            aria-label='View'
            className='shrink-0'
          />
        </div>
      </div>

      {isError && error && (
        <ErrorAlertWithRetry
          message={`Failed to load applications: ${error.message}`}
          onRetry={() => refetch()}
        />
      )}

      {isTruncated && !hasApplicationSearchQuery && (
        <Text size={TextSizes.SM} className='text-quaternary'>
          Showing {allApps.length} of {totalCount} applications. Search may not
          find the rest.
        </Text>
      )}

      {!isError && (
        <CatalogBody
          viewMode={viewMode}
          isLoading={isLoading}
          filter={filter}
          query={query}
          hasApplicationSearchQuery={hasApplicationSearchQuery}
          matches={matches}
          recentApps={recentApps}
          recentAppIds={recentAppIds}
          showRecent={showRecent}
          onApplicationClick={onApplicationClick}
          loadingAppId={loadingAppId}
        />
      )}
    </div>
  );
};
